/**
 * What the Overview's Selections card reads (ADR 0304).
 *
 * Reads; decides nothing. Which colour wins and how a colour is read out of
 * the handoff email are `jobColour` and `sendsSelections` in packages/core.
 */

import {
  jobColourOptions,
  type ColouredLine,
  type ColorProduct,
  type Job,
} from "@roof-hawk/core";

import { supabase, unwrap } from "./supabase";

export interface SelectionsData {
  /** The handoff email: the copy kept on the job, else the one Gmail synced. */
  sendsEmail: string | null;
  /** Newest material order first, each in its own line order. */
  orderLines: ColouredLine[];
  estimateLines: ColouredLine[];
  /** The colours the job's roof products come in, for the dropdown. */
  colourOptions: string[];
}

interface LineRow {
  product_id: string | null;
  color: string | null;
  sort_order: number | null;
}

/**
 * The handoff email for a job.
 *
 * The copy on the job is the one that counts: it is what was sent or marked
 * sent, and what somebody pasted in. Before the job kept one, a handoff sent
 * from the app still came back into the job's email history, so the newest
 * of those is the fallback. One copied into somebody's own Gmail came back
 * nowhere, and has to be pasted.
 */
export async function fetchSendsEmail(job: Job): Promise<string | null> {
  if (job.sends_email) return job.sends_email;
  const { data } = await supabase
    .from("email_messages")
    .select("body_text, sent_at, email_threads!inner(job_id)")
    .eq("email_threads.job_id", job.id)
    .eq("direction", "outbound")
    .ilike("subject", "New signed job%")
    .order("sent_at", { ascending: false })
    .limit(1);
  const row = (data?.[0] ?? null) as { body_text?: string | null } | null;
  return row?.body_text ?? null;
}

const byOrder = (a: LineRow, b: LineRow) =>
  (a.sort_order ?? 0) - (b.sort_order ?? 0);

/**
 * Everything the card shows, from the estimates that count — the active ones,
 * which a signature makes of whatever it bought — and their material orders.
 * Each part that fails is left out rather than the card.
 */
export async function fetchSelections(job: Job): Promise<SelectionsData> {
  const sendsEmail = await fetchSendsEmail(job).catch(() => null);

  const estimates = unwrap<{ id: string }[]>(
    await supabase
      .from("estimates")
      .select("id")
      .eq("job_id", job.id)
      .eq("is_active", true),
  );
  const ids = estimates.map((e) => e.id);
  if (ids.length === 0) {
    return { sendsEmail, orderLines: [], estimateLines: [], colourOptions: [] };
  }

  const [items, orders] = await Promise.all([
    supabase
      .from("estimate_items")
      .select("description, product_id, color, sort_order, work_doing")
      .in("estimate_id", ids),
    supabase
      .from("material_orders")
      .select(
        "created_at, items:material_order_items(name, product_id, color, sort_order)",
      )
      .in("estimate_id", ids)
      .order("created_at", { ascending: false }),
  ]);

  const itemRows = (
    (items.data ?? []) as (LineRow & {
      description: string | null;
      work_doing: boolean | null;
    })[]
  )
    .filter((row) => row.work_doing !== false)
    .sort(byOrder);
  const orderRows = (
    (orders.data ?? []) as { items: (LineRow & { name: string | null })[] }[]
  ).flatMap((order) => [...(order.items ?? [])].sort(byOrder));

  const productIds = [
    ...new Set(
      [...itemRows, ...orderRows]
        .map((row) => row.product_id)
        .filter((id): id is string => Boolean(id)),
    ),
  ];
  const products = productIds.length
    ? (((
        await supabase
          .from("products")
          .select("id, name, color_rule, color_options")
          .in("id", productIds)
      ).data ?? []) as ColorProduct[])
    : [];
  const byId = new Map(products.map((p) => [p.id, p]));
  const rule = (id: string | null) =>
    (id ? byId.get(id)?.color_rule : null) ?? null;

  const colourOptions = jobColourOptions(products);

  return {
    sendsEmail,
    orderLines: orderRows.map((row) => ({
      name: row.name ?? "",
      color: row.color,
      rule: rule(row.product_id),
    })),
    estimateLines: itemRows.map((row) => ({
      name: (row.description ?? "").split("\n")[0] ?? "",
      color: row.color,
      rule: rule(row.product_id),
    })),
    colourOptions,
  };
}
